/**
 * Render targets: what Chromium prints/screenshots, one PDF page or one flipbook image per target.
 *   cover         whole cover strip (panel order from doc.cover.binding) → cover.pdf when options.cover === 'separate'
 *   cover-front   page-sized window on the front cover            → first page of album.pdf when 'bound'
 *   cover-back    page-sized window on the back cover             → last page of album.pdf when 'bound'
 *   spread-{i}    spread i (single spreads shrink to one page)    → pageMode 'spread'
 *   page-{n}      book page n (1-based)                            → pageMode 'single'
 * Sizes are TRIM sizes; bleed is added by the render page when printReady.
 */
import { coverSize, spreadSize, type AlbumDocument, type ExportOptions } from '@freealbum/document';

export type TargetKind = 'cover' | 'cover-front' | 'cover-back' | 'spread' | 'page';

/**
 * NOTE: `@freealbum/render` exports a `RenderTarget` **string union** of target ids
 * (`'cover' | 'cover-front' | 'spread-${number}' | …`). This is the resolved **geometry** object for
 * the same vocabulary, hence the different name (docs/03-architecture.md §3.5).
 */

export interface RenderTargetGeometry {
  id: string;
  kind: TargetKind;
  /** index into doc.spreads; null for covers */
  spreadIndex: number | null;
  /** 1-based book page number for kind 'page' */
  pageNumber: number | null;
  /** visible window size (trim, mm) */
  widthMm: number;
  heightMm: number;
  /** full canvas the view renders (spread or cover strip) */
  canvasWidthMm: number;
  canvasHeightMm: number;
  /** x offset of the window inside the canvas (mm) */
  offsetXMm: number;
}

const TARGET_RE = /^(cover|cover-front|cover-back|spread-(\d+)|page-(\d+))$/;

/**
 * Reading order of the book, taken from the binding — the thing that physically decides it. A
 * right-to-left album reads right→left and puts page 1 on the LEFT. Kept in step with
 * `readingOrder()` in @freealbum/render/geometry: the renderer and the export planner must agree on
 * what `page-3` means.
 */
export function readingOrder(doc: Pick<AlbumDocument, 'cover' | 'meta'>): 'rtl' | 'ltr' {
  return doc.cover.binding ?? (doc.meta.lang === 'he' ? 'rtl' : 'ltr');
}

/** Sequence of (spreadIndex, side) for every physical inner page, in reading order. */
export function bookPages(doc: Pick<AlbumDocument, 'spreads' | 'cover' | 'meta'>): { spreadIndex: number; side: 'left' | 'right' }[] {
  const order: ('left' | 'right')[] = readingOrder(doc) === 'rtl' ? ['right', 'left'] : ['left', 'right'];
  const out: { spreadIndex: number; side: 'left' | 'right' }[] = [];
  doc.spreads.forEach((s, i) => {
    const sides = s.single ? [s.single] : order;
    for (const side of sides) out.push({ spreadIndex: i, side });
  });
  return out;
}

/** Resolve a target id against a document. Returns null for unknown ids / out-of-range indices. */
export function resolveTarget(doc: AlbumDocument, id: string): RenderTargetGeometry | null {
  const m = TARGET_RE.exec(id);
  if (!m) return null;
  const page = doc.page;
  if (id === 'cover' || id === 'cover-front' || id === 'cover-back') {
    const c = coverSize(doc);
    if (id === 'cover') {
      return { id, kind: 'cover', spreadIndex: null, pageNumber: null, widthMm: c.widthMm, heightMm: c.heightMm, canvasWidthMm: c.widthMm, canvasHeightMm: c.heightMm, offsetXMm: 0 };
    }
    return {
      id,
      kind: id,
      spreadIndex: null,
      pageNumber: null,
      widthMm: page.widthMm,
      heightMm: page.heightMm,
      canvasWidthMm: c.widthMm,
      canvasHeightMm: c.heightMm,
      // Panel order follows doc.cover.binding: 'rtl' → [front][spine][back], 'ltr' → [back][spine][front].
      offsetXMm: id === (doc.cover.binding === 'rtl' ? 'cover-front' : 'cover-back') ? 0 : page.widthMm + doc.cover.spineWidthMm,
    };
  }
  const s = spreadSize(doc);
  if (m[2] !== undefined) {
    const i = Number(m[2]);
    const spread = doc.spreads[i];
    if (!spread) return null;
    const side = spread.single;
    return {
      id,
      kind: 'spread',
      spreadIndex: i,
      pageNumber: null,
      widthMm: side ? page.widthMm : s.widthMm,
      heightMm: s.heightMm,
      canvasWidthMm: s.widthMm,
      canvasHeightMm: s.heightMm,
      offsetXMm: side === 'right' ? page.widthMm : 0,
    };
  }
  const n = Number(m[3]);
  const pages = bookPages(doc);
  const p = pages[n - 1];
  if (!p) return null;
  return {
    id,
    kind: 'page',
    spreadIndex: p.spreadIndex,
    pageNumber: n,
    widthMm: page.widthMm,
    heightMm: page.heightMm,
    canvasWidthMm: s.widthMm,
    canvasHeightMm: s.heightMm,
    offsetXMm: p.side === 'right' ? page.widthMm : 0,
  };
}

/** Inner-book targets in order for a page mode. */
export function innerTargets(doc: AlbumDocument, pageMode: ExportOptions['pageMode']): RenderTargetGeometry[] {
  const ids = pageMode === 'spread' ? doc.spreads.map((_, i) => `spread-${i}`) : bookPages(doc).map((_, i) => `page-${i + 1}`);
  return ids.map((id) => resolveTarget(doc, id)).filter((t): t is RenderTargetGeometry => t !== null);
}

export interface ExportPlan {
  /** targets merged, in order, into album.pdf */
  album: RenderTargetGeometry[];
  /** targets merged into cover.pdf (empty unless options.cover === 'separate' and the doc has a cover) */
  cover: RenderTargetGeometry[];
  /** flipbook images, in reading order: front cover, inner, back cover */
  flipbook: RenderTargetGeometry[];
}

/** Everything the export worker has to render for a job. */
export function planExport(doc: AlbumDocument, options: Pick<ExportOptions, 'pageMode' | 'cover' | 'flipbook'>): ExportPlan {
  const inner = innerTargets(doc, options.pageMode);
  const hasCover = doc.cover.enabled && options.cover !== 'none';
  const front = hasCover ? resolveTarget(doc, 'cover-front') : null;
  const back = hasCover ? resolveTarget(doc, 'cover-back') : null;
  const strip = hasCover ? resolveTarget(doc, 'cover') : null;
  const album = options.cover === 'bound' && front && back ? [front, ...inner, back] : inner;
  const cover = options.cover === 'separate' && strip ? [strip] : [];
  const flipbook = options.flipbook ? (front && back ? [front, ...inner, back] : inner) : [];
  return { album, cover, flipbook };
}

/** Flipbook image size for a target at a fixed width. */
export function flipbookSize(t: RenderTargetGeometry, widthPx: number): { w: number; h: number } {
  return { w: widthPx, h: Math.round((widthPx * t.heightMm) / t.widthMm) };
}

/** CSS px per mm at 96 dpi — what Chromium uses for `@page { size: Wmm Hmm }`. */
export const CSS_PX_PER_MM = 96 / 25.4;
